import { Condition, WaitQueue } from "./condition";
import { Resource } from "./resource";

export class Semaphore {
  #count: number;
  readonly #waiters = new WaitQueue<void>();

  constructor(count: number) {
    this.#count = count;
  }

  acquire(signal?: AbortSignal): Promise<void> {
    if (--this.#count < 0) {
      return this.#waiters.wait(signal);
    }
    return Promise.resolve();
  }

  /**
   * Try to acquire the semaphore, but skip if there's already a waiter.
   * This prevents queue buildup for hot resources - at most 1 holder + 1 waiter.
   * @returns 'skipped' if there's already a waiter, otherwise a Promise that resolves when acquired
   */
  acquireUnlessWaiterExists(signal?: AbortSignal): "skipped" | Promise<void> {
    if (this.#count < 0) {
      return "skipped";
    }
    return this.acquire(signal);
  }

  get count(): number {
    return this.#count;
  }

  resource(signal?: AbortSignal): Resource<void> {
    return async (fn) => {
      await this.acquire(signal);
      try {
        return await fn();
      } finally {
        this.release();
      }
    };
  }

  release(): void {
    this.#count++;
    this.#waiters.release();
  }
}

export class CountdownLatch {
  #count: number;
  readonly #latch = new Condition<void>();

  constructor(count: number) {
    if (count < 0) {
      throw new Error("CountdownLatch count must be non-negative");
    }
    this.#count = count;
  }

  countDown(): void {
    if (this.#count > 0) {
      this.#count--;
      if (this.#count === 0) {
        this.#latch.resolve();
      }
    }
  }

  reject(reason: any): void {
    this.#latch.reject(reason);
  }

  wait(): Promise<void> {
    return this.#latch.result;
  }
}
